import Phaser from 'phaser';
import { formatNumber } from '../utils/formatNumber';
import { ACHIEVEMENT_BONUSES } from '../balance';

// ---- Types ----

export type AchievementBonusType = 'damage' | 'gold' | 'wallHP' | 'clickDamage' | 'builderRepair';

export interface AchievementDef {
  id: string;
  name: string;
  description: string;
  icon: string;
  target: number;
  bonusType: AchievementBonusType;
  bonusPercent: number; // e.g. 0.5 means +0.5%
  category: string;
}

export interface Achievement extends AchievementDef {
  unlocked: boolean;
  progress: number;
}

// ---- Bonus type display info ----

const BONUS_LABELS: Record<AchievementBonusType, string> = {
  damage: 'Damage',
  gold: 'Gold',
  wallHP: 'Wall HP',
  clickDamage: 'Click Dmg',
  builderRepair: 'Repair',
};

const BONUS_COLORS: Record<AchievementBonusType, string> = {
  damage: '#ff6666',
  gold: '#ffdd44',
  wallHP: '#6688ff',
  clickDamage: '#ffffff',
  builderRepair: '#44cc44',
};

// ---- Achievement generator ----

function generateTiered(
  category: string,
  descTemplate: string, // "Kill {n} enemies"
  icon: string,
  targets: number[],
  bonusType: AchievementBonusType,
  bonusPercent: number | number[], // single value or per-tier array
  names: string[],
): AchievementDef[] {
  return targets.map((target, i) => ({
    id: `${category}_${i}`,
    name: names[i] || `${category} ${i + 1}`,
    description: descTemplate.replace('{n}', formatNumber(target)),
    icon,
    target,
    bonusType,
    bonusPercent: Array.isArray(bonusPercent) ? (bonusPercent[i] ?? bonusPercent[bonusPercent.length - 1]) : bonusPercent,
    category,
  }));
}

// ---- Achievement definitions ----

const ALL_DEFS: AchievementDef[] = [
  // Kills — damage bonus (targets & bonusPercent in src/balance.ts ACHIEVEMENT_BONUSES.kills)
  ...generateTiered('kills', 'Kill {n} enemies', '⚔️',
    [...ACHIEVEMENT_BONUSES.kills.targets], 'damage', [...ACHIEVEMENT_BONUSES.kills.bonusPercent],
    ['First Blood', 'Skirmisher', 'Warrior', 'Veteran', 'Champion', 'Warlord', 'Legend', 'Mythic Slayer', 'Godlike', 'Apocalypse', 'Extinction'],
  ),
  ...generateTiered('boss_kills', 'Kill {n} bosses', '💀',
    [...ACHIEVEMENT_BONUSES.boss_kills.targets], 'damage', [...ACHIEVEMENT_BONUSES.boss_kills.bonusPercent],
    ['Boss Slayer', 'Boss Hunter', 'Boss Crusher', 'Boss Destroyer', 'Boss Nightmare', 'Boss Annihilator', 'Boss Overlord', 'Boss Extinction'],
  ),
  ...generateTiered('wave', 'Reach wave {n}', '🌊',
    [...ACHIEVEMENT_BONUSES.wave.targets], 'gold', [...ACHIEVEMENT_BONUSES.wave.bonusPercent],
    ['Survivor', 'Endurance', 'Steadfast', 'Unstoppable', 'Unyielding', 'Immortal', 'Timeless', 'Eternal', 'Infinite', 'Transcendent'],
  ),
  ...generateTiered('clicks', 'Click {n} times', '👆',
    [...ACHIEVEMENT_BONUSES.clicks.targets], 'clickDamage', [...ACHIEVEMENT_BONUSES.clicks.bonusPercent],
    ['Tapper', 'Clicker', 'Rapid Fire', 'Trigger Happy', 'Machine Gun', 'Turbo Clicker', 'Click Lord', 'Click Titan', 'Click God'],
  ),
  ...generateTiered('gold_earned', 'Earn {n} total gold', '💰',
    [...ACHIEVEMENT_BONUSES.gold_earned.targets], 'gold', [...ACHIEVEMENT_BONUSES.gold_earned.bonusPercent],
    ['Pocket Change', 'Saver', 'Wealthy', 'Rich', 'Tycoon', 'Magnate', 'Billionaire', 'Trillionaire'],
  ),
  ...generateTiered('gold_held', 'Hold {n} gold at once', '💎',
    [...ACHIEVEMENT_BONUSES.gold_held.targets], 'gold', [...ACHIEVEMENT_BONUSES.gold_held.bonusPercent],
    ['Piggy Bank', 'Stash', 'Treasury', 'Vault', 'Fort Knox', 'Dragon Hoard', 'Cosmic Wealth'],
  ),
  ...generateTiered('wall_damage', 'Wall absorbs {n} damage', '🧱',
    [...ACHIEVEMENT_BONUSES.wall_damage.targets], 'wallHP', [...ACHIEVEMENT_BONUSES.wall_damage.bonusPercent],
    ['Scratched', 'Battered', 'Battle-Scarred', 'War-Torn', 'Siege Survivor', 'Unbreakable', 'Eternal Fortress'],
  ),
  ...generateTiered('repair', 'Repair {n} total HP', '🔧',
    [...ACHIEVEMENT_BONUSES.repair.targets], 'builderRepair', [...ACHIEVEMENT_BONUSES.repair.bonusPercent],
    ['Handyman', 'Repairman', 'Craftsman', 'Master Builder', 'Architect', 'Grand Architect', 'Divine Mason'],
  ),
  ...generateTiered('units_owned', 'Own {n} total units', '👥',
    [...ACHIEVEMENT_BONUSES.units_owned.targets], 'damage', [...ACHIEVEMENT_BONUSES.units_owned.bonusPercent],
    ['Small Squad', 'Platoon', 'Company', 'Battalion', 'Regiment', 'Legion', 'Infinite Army'],
  ),
  ...generateTiered('rock_thrower_owned', 'Own {n} Rock Throwers', '🪨',
    [...ACHIEVEMENT_BONUSES.rock_thrower_owned.targets], 'damage', [...ACHIEVEMENT_BONUSES.rock_thrower_owned.bonusPercent],
    ['Stone Picker', 'Rock Squad', 'Boulder Brigade', 'Avalanche', 'Mountain Fury', 'Meteor Storm'],
  ),
  ...generateTiered('archer_owned', 'Own {n} Archers', '🏹',
    [...ACHIEVEMENT_BONUSES.archer_owned.targets], 'damage', [...ACHIEVEMENT_BONUSES.archer_owned.bonusPercent],
    ['Bowman', 'Archer Squad', 'Arrow Storm', 'Rain of Arrows', 'Arrow Apocalypse', 'Arrow Infinity'],
  ),
  ...generateTiered('club_swinger_owned', 'Own {n} Club Swingers', '🏏',
    [...ACHIEVEMENT_BONUSES.club_swinger_owned.targets], 'damage', [...ACHIEVEMENT_BONUSES.club_swinger_owned.bonusPercent],
    ['Brawler', 'Club Squad', 'Smash Brigade', 'Crusher Corps', 'Demolition Force', 'Annihilation Army'],
  ),
  ...generateTiered('sling_thrower_owned', 'Own {n} Sling Throwers', '🎯',
    [...ACHIEVEMENT_BONUSES.sling_thrower_owned.targets], 'damage', [...ACHIEVEMENT_BONUSES.sling_thrower_owned.bonusPercent],
    ['Slinger', 'Sling Squad', 'Sling Brigade', 'Sling Masters', 'Sling Legends', 'Sling Gods'],
  ),
  ...generateTiered('spear_thrower_owned', 'Own {n} Spear Throwers', '🔱',
    [...ACHIEVEMENT_BONUSES.spear_thrower_owned.targets], 'damage', [...ACHIEVEMENT_BONUSES.spear_thrower_owned.bonusPercent],
    ['Javelin Toss', 'Spear Squad', 'Spear Wall', 'Phalanx', 'Spear Storm', 'Spear Infinity'],
  ),
  ...generateTiered('builder_owned', 'Own {n} Builders', '🔨',
    [...ACHIEVEMENT_BONUSES.builder_owned.targets], 'builderRepair', [...ACHIEVEMENT_BONUSES.builder_owned.bonusPercent],
    ['Apprentice', 'Builder Crew', 'Construction Team', 'Builder Army', 'Builder Legion', 'Builder Empire'],
  ),
  ...generateTiered('longbow_archer_owned', 'Own {n} Longbow Archers', '🎯',
    [...ACHIEVEMENT_BONUSES.longbow_archer_owned.targets], 'damage', [...ACHIEVEMENT_BONUSES.longbow_archer_owned.bonusPercent],
    ['Marksman', 'Longbow Squad', 'Longbow Brigade', 'Sniper Corps', 'Longbow Legion', 'Longbow Gods'],
  ),
  ...generateTiered('crow_trainer_owned', 'Own {n} Crow Trainers', '🐦',
    [...ACHIEVEMENT_BONUSES.crow_trainer_owned.targets], 'gold', [...ACHIEVEMENT_BONUSES.crow_trainer_owned.bonusPercent],
    ['Bird Keeper', 'Crow Handler', 'Crow Master', 'Crow Commander', 'Crow Overlord', 'Crow Emperor'],
  ),
  ...generateTiered('beehive_thrower_owned', 'Own {n} Beehive Throwers', '🐝',
    [...ACHIEVEMENT_BONUSES.beehive_thrower_owned.targets], 'damage', [...ACHIEVEMENT_BONUSES.beehive_thrower_owned.bonusPercent],
    ['Beekeeper', 'Hive Tosser', 'Swarm Master', 'Bee Commander', 'Hive Overlord', 'Bee Emperor'],
  ),
  ...generateTiered('trebuchet_owned', 'Own {n} Trebuchets', '🏗️',
    [...ACHIEVEMENT_BONUSES.trebuchet_owned.targets], 'damage', [...ACHIEVEMENT_BONUSES.trebuchet_owned.bonusPercent],
    ['Siege Apprentice', 'Siege Operator', 'Siege Master', 'Siege Commander', 'Siege Lord', 'Siege God'],
  ),
  ...generateTiered('crossbow_owned', 'Own {n} Crossbows', '🔫',
    [...ACHIEVEMENT_BONUSES.crossbow_owned.targets], 'damage', [...ACHIEVEMENT_BONUSES.crossbow_owned.bonusPercent],
    ['Bolt Shooter', 'Crossbow Squad', 'Crossbow Brigade', 'Bolt Storm', 'Crossbow Legion', 'Crossbow Infinity'],
  ),
  ...generateTiered('firefly_nest_owned', 'Own {n} Firefly Nests', '🔥',
    [...ACHIEVEMENT_BONUSES.firefly_nest_owned.targets], 'damage', [...ACHIEVEMENT_BONUSES.firefly_nest_owned.bonusPercent],
    ['Spark Keeper', 'Firefly Breeder', 'Firefly Swarm', 'Inferno Nests', 'Firefly Overlord', 'Firefly Apocalypse'],
  ),
  ...generateTiered('druid_owned', 'Own {n} Druids', '🌿',
    [...ACHIEVEMENT_BONUSES.druid_owned.targets], 'damage', [...ACHIEVEMENT_BONUSES.druid_owned.bonusPercent],
    ['Seedling', 'Herbalist', 'Grove Keeper', 'Forest Sage', 'Druid Elder', 'Archdruid'],
  ),
  ...generateTiered('lightning_wizard_owned', 'Own {n} Lightning Wizards', '⚡',
    [...ACHIEVEMENT_BONUSES.lightning_wizard_owned.targets], 'damage', [...ACHIEVEMENT_BONUSES.lightning_wizard_owned.bonusPercent],
    ['Spark Mage', 'Storm Acolyte', 'Thunder Mage', 'Storm Master', 'Lightning Lord', 'Storm God'],
  ),
  ...generateTiered('boomerang_thrower_owned', 'Own {n} Boomerang Throwers', '🪃',
    [...ACHIEVEMENT_BONUSES.boomerang_thrower_owned.targets], 'damage', [...ACHIEVEMENT_BONUSES.boomerang_thrower_owned.bonusPercent],
    ['Boomerang Kid', 'Return Squad', 'Whirling Wall', 'Arc Masters', 'Cyclone Corps', 'Eternal Vortex'],
  ),
  ...generateTiered('prestige_count', 'Prestige {n} times', '🔄',
    [...ACHIEVEMENT_BONUSES.prestige_count.targets], 'damage', [...ACHIEVEMENT_BONUSES.prestige_count.bonusPercent],
    ['First Reset', 'Veteran', 'Bronze Age', 'Iron Age', 'Steel Legacy', 'Eternal Cycle', 'Infinite Loop', 'Ascendant'],
  ),
  ...generateTiered('legacy_earned', 'Earn {n} total legacy', '🏛️',
    [...ACHIEVEMENT_BONUSES.legacy_earned.targets], 'gold', [...ACHIEVEMENT_BONUSES.legacy_earned.bonusPercent],
    ['Legacy Born', 'Heirloom', 'Dynasty', 'Empire', 'Ancestral Might', 'Eternal Legacy', 'Infinite Legacy', 'Cosmic Legacy'],
  ),
  ...generateTiered('boulder_kills', 'Destroy {n} boulders', '🪨',
    [...ACHIEVEMENT_BONUSES.boulder_kills.targets], 'wallHP', [...ACHIEVEMENT_BONUSES.boulder_kills.bonusPercent],
    ['Rock Stopper', 'Boulder Buster', 'Stone Crusher', 'Rockslide Victor', 'Boulder Slayer', 'Granite God', 'Mountain Killer', 'Geological Genocide'],
  ),
  ...generateTiered('brute_kills', 'Kill {n} brutes', '💪',
    [...ACHIEVEMENT_BONUSES.brute_kills.targets], 'damage', [...ACHIEVEMENT_BONUSES.brute_kills.bonusPercent],
    ['Brute Beater', 'Brawl Winner', 'Brute Hunter', 'Brute Crusher', 'Brute Destroyer', 'Brute Nightmare', 'Brute Legend', 'Brute Extinction'],
  ),
  ...generateTiered('shaman_kills', 'Kill {n} shamans', '🧙',
    [...ACHIEVEMENT_BONUSES.shaman_kills.targets], 'gold', [...ACHIEVEMENT_BONUSES.shaman_kills.bonusPercent],
    ['Shaman Slayer', 'Witch Hunter', 'Mystic Killer', 'Spirit Breaker', 'Hex Destroyer', 'Totem Crusher', 'Shaman Legend', 'Shaman Extinction'],
  ),
  ...generateTiered('skills_used', 'Use skills {n} times', '✨',
    [...ACHIEVEMENT_BONUSES.skills_used.targets], 'clickDamage', [...ACHIEVEMENT_BONUSES.skills_used.bonusPercent],
    ['Apprentice Caster', 'Spellcaster', 'Skilled Mage', 'Arcane Master', 'Spell Lord', 'Magic Legend', 'Arcane God', 'Infinite Caster'],
  ),
  ...generateTiered('click_total_damage', 'Deal {n} total click damage', '👊',
    [...ACHIEVEMENT_BONUSES.click_total_damage.targets], 'clickDamage', [...ACHIEVEMENT_BONUSES.click_total_damage.bonusPercent],
    ['Light Tap', 'Solid Strike', 'Power Punch', 'Crushing Blow', 'Devastating Impact', 'World Punch', 'Universe Strike', 'Omega Punch'],
  ),
  ...generateTiered('gold_single_run', 'Earn {n} gold in one run', '🏆',
    [...ACHIEVEMENT_BONUSES.gold_single_run.targets], 'gold', [...ACHIEVEMENT_BONUSES.gold_single_run.bonusPercent],
    ['Starter Haul', 'Small Fortune', 'Big Earner', 'Gold Rush', 'Tycoon Run', 'Legendary Haul', 'Godlike Run', 'Infinite Wealth'],
  ),
  ...generateTiered('legacy_spent', 'Spend {n} legacy', '💸',
    [...ACHIEVEMENT_BONUSES.legacy_spent.targets], 'builderRepair', [...ACHIEVEMENT_BONUSES.legacy_spent.bonusPercent],
    ['First Investment', 'Elder Patron', 'Legacy Spender', 'Council Patron', 'Elder Benefactor', 'Legacy Lord', 'Council Master', 'Eternal Patron'],
  ),
];

// Build category → achievement ID mappings
const CATEGORY_MAP: Record<string, string[]> = {};
for (const def of ALL_DEFS) {
  if (!CATEGORY_MAP[def.category]) CATEGORY_MAP[def.category] = [];
  CATEGORY_MAP[def.category].push(def.id);
}

// ---- Achievement Manager ----

export class AchievementManager extends Phaser.Events.EventEmitter {
  private achievements: Map<string, Achievement> = new Map();
  private scene: Phaser.Scene | null = null;
  private notificationQueue: Achievement[] = [];
  private isShowingNotification: boolean = false;

  constructor() {
    super();
    this.initAchievements();
  }

  private initAchievements(): void {
    for (const def of ALL_DEFS) {
      this.achievements.set(def.id, {
        ...def,
        unlocked: false,
        progress: 0,
      });
    }
  }

  setScene(scene: Phaser.Scene): void {
    this.scene = scene;
  }

  checkProgress(category: string, value: number): void {
    const ids = CATEGORY_MAP[category];
    if (!ids) return;

    for (const id of ids) {
      const ach = this.achievements.get(id);
      if (ach && !ach.unlocked) {
        ach.progress = value;
        if (ach.progress >= ach.target) {
          ach.unlocked = true;
          this.emit('unlock', ach);
          this.queueNotification(ach);
        }
      }
    }
  }

  getBonuses(): Record<AchievementBonusType, number> {
    const bonuses: Record<AchievementBonusType, number> = {
      damage: 0,
      gold: 0,
      wallHP: 0,
      clickDamage: 0,
      builderRepair: 0,
    };

    for (const ach of this.achievements.values()) {
      if (ach.unlocked) {
        bonuses[ach.bonusType] += ach.bonusPercent / 100;
      }
    }

    return bonuses;
  }

  private notificationEl: HTMLElement | null = null;
  private stackEls: HTMLElement[] = [];

  private queueNotification(achievement: Achievement): void {
    this.notificationQueue.push(achievement);
    if (!this.isShowingNotification) {
      this.showNextNotification();
    } else {
      this.updateStackDisplay();
    }
  }

  private updateStackDisplay(): void {
    if (!this.isShowingNotification) return;

    // Clear existing stack elements
    for (const el of this.stackEls) el.remove();
    this.stackEls = [];

    const maxStacked = Math.min(this.notificationQueue.length, 2);
    for (let i = 1; i <= maxStacked; i++) {
      const stackCard = document.createElement('div');
      stackCard.style.cssText = `
        position: absolute;
        top: ${-i * 6}px;
        left: ${i * 6}px;
        right: ${i * 6}px;
        height: 60px;
        background: rgba(26, 26, 46, 0.95);
        border: 2px solid rgba(170, 136, 0, 0.8);
        border-radius: 8px;
        z-index: ${-i};
        opacity: 0;
        transition: opacity 0.2s ease;
      `;
      if (this.notificationEl) {
        this.notificationEl.appendChild(stackCard);
        requestAnimationFrame(() => { stackCard.style.opacity = '1'; });
      }
      this.stackEls.push(stackCard);
    }
  }

  private showNextNotification(): void {
    if (this.notificationQueue.length === 0) {
      this.isShowingNotification = false;
      return;
    }

    this.isShowingNotification = true;
    const ach = this.notificationQueue.shift()!;
    const bonusColor = BONUS_COLORS[ach.bonusType];
    const bonusLabel = BONUS_LABELS[ach.bonusType];

    // Create notification container — position inside game wrapper to center over game canvas
    const gameWrapper = document.getElementById('game-wrapper');

    // Dynamically compute position below the boss HP bar (y=52, height=16 in game coords)
    // accounting for canvas letterboxing and scale on all screen sizes
    let topPx = '13%'; // fallback if canvas not found
    if (gameWrapper) {
      const canvas = gameWrapper.querySelector('canvas') as HTMLElement | null;
      if (canvas) {
        const wrapperRect = gameWrapper.getBoundingClientRect();
        const canvasRect = canvas.getBoundingClientRect();
        const canvasTopInWrapper = canvasRect.top - wrapperRect.top;
        const scaleY = canvasRect.height / 600; // GAME_HEIGHT = 600
        const bossBarBottomY = 68; // boss bar at y=52, height=16
        const padding = 8;
        topPx = `${canvasTopInWrapper + (bossBarBottomY + padding) * scaleY}px`;
      }
    }

    const container = document.createElement('div');
    container.style.cssText = `
      position: absolute;
      top: ${topPx};
      left: 50%;
      transform: translateX(-50%);
      z-index: 9999;
      pointer-events: auto;
    `;
    (gameWrapper || document.body).appendChild(container);
    // Needed for absolute positioning within game-wrapper
    if (gameWrapper) gameWrapper.style.position = 'relative';

    // Main card
    const card = document.createElement('div');
    card.style.cssText = `
      position: relative;
      width: 320px;
      display: flex;
      align-items: center;
      padding: 10px 14px;
      background: rgba(26, 26, 46, 0.95);
      border: 2px solid rgba(255, 215, 0, 0.8);
      border-radius: 8px;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5), 0 0 15px rgba(255, 215, 0, 0.15);
      opacity: 0;
      transform: translateY(-10px);
      transition: opacity 0.3s ease, transform 0.3s ease;
    `;

    card.innerHTML = `
      <div style="font-size: 28px; margin-right: 12px; flex-shrink: 0;">${ach.icon}</div>
      <div style="flex: 1; min-width: 0;">
        <div style="font-size: 14px; font-weight: bold; color: #ffd700; font-family: Arial, sans-serif;">🏆 ${ach.name}</div>
        <div style="font-size: 11px; color: #aaa; font-family: Arial, sans-serif; margin-top: 2px;">${ach.description}</div>
      </div>
      <div style="text-align: center; margin-left: 8px; flex-shrink: 0;">
        <div style="font-size: 12px; font-weight: bold; color: ${bonusColor}; font-family: Arial, sans-serif;">+${ach.bonusPercent}%</div>
        <div style="font-size: 10px; color: ${bonusColor}; font-family: Arial, sans-serif; opacity: 0.8;">${bonusLabel}</div>
      </div>
    `;

    // Close button
    const closeBtn = document.createElement('div');
    closeBtn.style.cssText = `
      position: absolute;
      top: -8px;
      right: -8px;
      width: 22px;
      height: 22px;
      background: #333344;
      border: 1px solid rgba(255, 255, 255, 0.5);
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      font-size: 12px;
      font-weight: bold;
      color: #fff;
      font-family: Arial, sans-serif;
      z-index: 1;
      transition: background 0.15s;
    `;
    closeBtn.textContent = '✕';
    closeBtn.addEventListener('mouseenter', () => { closeBtn.style.background = '#555566'; });
    closeBtn.addEventListener('mouseleave', () => { closeBtn.style.background = '#333344'; });

    card.appendChild(closeBtn);
    container.appendChild(card);
    this.notificationEl = container;

    // Animate in
    requestAnimationFrame(() => {
      card.style.opacity = '1';
      card.style.transform = 'translateY(0)';
    });

    // Show stack if needed
    this.updateStackDisplay();

    // Close handler
    const dismiss = () => {
      closeBtn.removeEventListener('click', dismiss);
      card.style.opacity = '0';
      card.style.transform = 'translateY(-10px)';
      for (const el of this.stackEls) el.style.opacity = '0';
      setTimeout(() => {
        container.remove();
        this.notificationEl = null;
        this.stackEls = [];
        this.showNextNotification();
      }, 250);
    };

    closeBtn.addEventListener('click', dismiss);
  }

  getAchievements(): Achievement[] {
    return Array.from(this.achievements.values());
  }

  getCategories(): string[] {
    return Object.keys(CATEGORY_MAP);
  }

  getAchievementsByCategory(category: string): Achievement[] {
    const ids = CATEGORY_MAP[category] || [];
    return ids.map(id => this.achievements.get(id)!).filter(Boolean);
  }

  getUnlockedCount(): number {
    let count = 0;
    for (const ach of this.achievements.values()) {
      if (ach.unlocked) count++;
    }
    return count;
  }

  getTotalCount(): number {
    return this.achievements.size;
  }

  getBonusLabel(type: AchievementBonusType): string {
    return BONUS_LABELS[type];
  }

  getBonusColor(type: AchievementBonusType): string {
    return BONUS_COLORS[type];
  }

  // ---- Serialization ----

  getState(): Record<string, { unlocked: boolean; progress: number }> {
    const state: Record<string, { unlocked: boolean; progress: number }> = {};
    for (const [id, ach] of this.achievements) {
      state[id] = { unlocked: ach.unlocked, progress: ach.progress };
    }
    return state;
  }

  loadState(state: Record<string, { unlocked: boolean; progress: number }>): void {
    for (const [id, data] of Object.entries(state)) {
      const ach = this.achievements.get(id);
      if (ach) {
        ach.unlocked = data.unlocked;
        ach.progress = data.progress;
      }
    }
  }
}

// Category display names for UI
export const CATEGORY_DISPLAY_NAMES: Record<string, string> = {
  kills: 'Kills',
  boss_kills: 'Boss Kills',
  wave: 'Waves',
  clicks: 'Clicks',
  gold_earned: 'Gold Earned',
  gold_held: 'Gold Held',
  wall_damage: 'Wall Damage',
  repair: 'Repairs',
  units_owned: 'Total Units',
  rock_thrower_owned: 'Rock Throwers',
  archer_owned: 'Archers',
  club_swinger_owned: 'Club Swingers',
  sling_thrower_owned: 'Sling Throwers',
  spear_thrower_owned: 'Spear Throwers',
  builder_owned: 'Builders',
  longbow_archer_owned: 'Longbow Archers',
  crow_trainer_owned: 'Crow Trainers',
  beehive_thrower_owned: 'Beehive Throwers',
  trebuchet_owned: 'Trebuchets',
  crossbow_owned: 'Crossbows',
  firefly_nest_owned: 'Firefly Nests',
  druid_owned: 'Druids',
  lightning_wizard_owned: 'Lightning Wizards',
  boomerang_thrower_owned: 'Boomerang Throwers',
  prestige_count: 'Prestiges',
  legacy_earned: 'Legacy Earned',
  boulder_kills: 'Boulder Kills',
  brute_kills: 'Brute Kills',
  shaman_kills: 'Shaman Kills',
  skills_used: 'Skills Used',
  click_total_damage: 'Click Damage',
  gold_single_run: 'Gold Per Run',
  legacy_spent: 'Legacy Spent',
};

// Singleton instance
export const achievementManager = new AchievementManager();
